import React, { useCallback, useRef } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import ArrowDownIcon from '@wix/da-ds/pkg/Icons/ArrowDown';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import PositionOnlyPopperPopover from '@wix/da-ds/pkg/popper/presets/PositionOnlyPopperPopover';
import DSModal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import Sticky from '@wix/da-ds/pkg/Sticky';
import FolderDropdownDesktopMenu from '../FolderDropdownDesktop/FolderDropdownDesktopMenu';
import { Props as FolderDropdownProps } from '../types';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { usePopover } from '../../Popovers/redux/hooks';
import { PopoverType } from '../../Popovers/redux/types';
import { Url } from '@wix/da-url';
import { GallectionType } from '../../types/gallection';
import AddIcon from '@wix/da-ds/pkg/Icons/24x24/Add';
import StudioMultiIcon from '@wix/da-ds/pkg/Icons/24x24/StudioMulti';
import {
  AddNewFolderClickBiEvent,
  ManageInStudioClickBiEvent,
  BiContextProvidedValue,
  BiData,
} from '@wix/da-bi/pkg/events';
import { ResourceType } from '../../types/resource';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';

import cssVars from '@wix/da-ds/src/styles/variables.scss';
import folderDropdownMobileStyles from './FolderDropdownMobile.scss';

export interface Props {
  offsetTop?: number;
  isCurrentUserProfileOwner?: boolean;
}

const FolderDropdownMobileV3: React.FC<Props & FolderDropdownProps> = ({
  id,
  offsetTop = 0,
  isCurrentUserProfileOwner,
  folders,
  folderNamePostfixIcon,
  disabled,
  currentFolder,
  setSelectedFolderId,
  arrowIconSize = IconSize.SMALL,
  showCurrentFolderTotal,
  isLoading,
  hasMore,
  fetchMore,
}) => {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const headerHeight = parseInt(cssVars['site-header-height-mobile']);
  const { popover, pushPopover, removePopover } = usePopover(
    PopoverType.GALLECTION_FOLDER
  );
  const selectFolder = useCallback(
    (folderId: number) => {
      setSelectedFolderId(folderId);
      removePopover();
    },
    [removePopover, setSelectedFolderId]
  );
  const togglePopover = () => {
    if (popover.isOpen) {
      removePopover();
    } else {
      pushPopover();
    }
  };

  const showStudioButtons =
    isCurrentUserProfileOwner &&
    currentFolder &&
    currentFolder.type === GallectionType.GALLERY;

  return (
    <PositionOnlyPopperPopover
      popoverContent={
        <DSModal
          isOpen
          heading={t('gallectionDropdown.title.mobile')}
          contentClassName={folderDropdownMobileStyles['content']}
          withScrollableContentWrapper={false}
          useFixedMobileHeight
          onRequestClose={togglePopover}
        >
          {showStudioButtons && (
            <div className={folderDropdownMobileStyles['studio-buttons']}>
              <PlainButtonOrLink
                href={`${Url.studio({ published: 'galleries' })}?new=1`}
                className={folderDropdownMobileStyles['studio-button']}
                biData={BiData<AddNewFolderClickBiEvent>({
                  evid: 40,
                  typeid: ResourceType.GALLERY,
                  itemid: currentFolder?.folderId,
                  sectionname: BiContextProvidedValue,
                  widgetname: BiContextProvidedValue,
                })}
              >
                <AddIcon
                  size={IconSize.MEDIUM}
                  className={folderDropdownMobileStyles['studio-button-icon']}
                />
                {t('common.createGallery')}
              </PlainButtonOrLink>
              <PlainButtonOrLink
                href={Url.studio({ published: 'galleries' })}
                className={folderDropdownMobileStyles['studio-button']}
                biData={BiData<ManageInStudioClickBiEvent>({
                  evid: 15,
                  link_name: t('common.manageInStudio'),
                  link_url: Url.studio({ published: 'galleries' }),
                  widgetname: BiContextProvidedValue,
                  sectionname: BiContextProvidedValue,
                })}
              >
                <StudioMultiIcon
                  className={folderDropdownMobileStyles['studio-button-icon']}
                  size={IconSize.MEDIUM}
                />
                {t('common.manageInStudio')}
              </PlainButtonOrLink>
            </div>
          )}
          <FolderDropdownDesktopMenu
            isOpen
            id={id}
            className={classnames(
              folderDropdownMobileStyles['modal-menu'],
              showStudioButtons &&
                folderDropdownMobileStyles['with-studio-buttons']
            )}
            folders={folders}
            setSelectedFolderId={selectFolder}
            currentFolder={currentFolder}
            hasMore={hasMore}
            fetchMore={fetchMore}
          />
          {isLoading && <LoadingIndicator coverMode view="llama" />}
        </DSModal>
      }
      isVisible={popover.isOpen}
    >
      <div
        className={classnames(
          folderDropdownMobileStyles['root'],
          disabled && folderDropdownMobileStyles['disabled']
        )}
        ref={rootRef}
      >
        <Sticky
          className={classnames('sticky', folderDropdownMobileStyles['sticky'])}
          marginTop={offsetTop + headerHeight}
        >
          {isStuck => (
            <div
              className={classnames(
                folderDropdownMobileStyles['toggle'],
                isStuck && folderDropdownMobileStyles['stuck']
              )}
              aria-hidden="true"
              onClick={togglePopover}
            >
              <div
                className={classnames(
                  folderDropdownMobileStyles['title-item'] as any,
                  folderNamePostfixIcon &&
                    folderDropdownMobileStyles['title-with-icon']
                )}
              >
                <div className={folderDropdownMobileStyles['title']}>
                  {currentFolder && currentFolder.name}
                </div>
                {showCurrentFolderTotal && currentFolder && (
                  <span className={folderDropdownMobileStyles['folder-total']}>
                    {currentFolder?.totalItemCount}
                  </span>
                )}
                {folderNamePostfixIcon}
              </div>
              <ArrowDownIcon
                className={classnames(
                  folderDropdownMobileStyles['icon-arrow'],
                  popover.isOpen && folderDropdownMobileStyles['is-open']
                )}
                size={arrowIconSize}
              />
            </div>
          )}
        </Sticky>
      </div>
    </PositionOnlyPopperPopover>
  );
};
FolderDropdownMobileV3.displayName = 'FolderDropdownMobileV3';

export default FolderDropdownMobileV3;
